import { z } from 'zod'

import {
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  messageDirectionSchema,
  moneyPaisa,
  noteSchema,
  searchQuerySchema,
  slugSchema,
  ticketSourceSchema,
  ticketStatusSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * P15 · Support (screen 09).
 *
 * Most tickets arrive on WhatsApp, which is why `source` exists and why
 * `raisedByName` is free text: the man messaging about his wanda bill may not
 * be the account holder, and refusing to record the conversation until he is
 * would simply lose it.
 */
export const ticketSchema = z.object({
  id: idSchema,
  number: z.number().int().positive(),
  tenantId: idSchema.nullish(),
  tenantName: z.string().nullish(),
  tenantSlug: slugSchema.nullish(),
  onTrial: z.boolean().optional(),
  subject: z.string(),
  source: ticketSourceSchema,
  status: ticketStatusSchema,
  category: z.string().nullish(),
  raisedByName: z.string(),
  raisedByUserId: idSchema.nullish(),
  assignedToUserId: idSchema.nullish(),
  assignedToName: z.string().nullish(),
  openedAt: isoInstantSchema,
  firstResponseAt: isoInstantSchema.nullish(),
  closedAt: isoInstantSchema.nullish(),
  /** How long the trader has been waiting, in minutes. Server-computed. */
  waitingMinutes: z.number().int().nonnegative(),
  /**
   * The trader's own words, quoted on the card. Support answers questions like
   * "why is my feed cost higher this month" and the quote is what makes the
   * ticket answerable without opening the book (which would need P5).
   */
  quote: z.string().nullish(),
  /** A costing explanation attached to the ticket, when one was produced. */
  breakdown: z
    .array(z.object({ label: labelSchema, amountPaisa: moneyPaisa() }))
    .optional(),
})

export const ticketMessageSchema = z.object({
  id: idSchema,
  ticketId: idSchema,
  body: z.string(),
  direction: messageDirectionSchema,
  authorUserId: idSchema.nullish(),
  authorName: z.string().nullish(),
  sentAt: isoInstantSchema,
})

export const ticketListQuery = cursorQuerySchema.extend({
  status: ticketStatusSchema.optional(),
  source: ticketSourceSchema.optional(),
  tenantId: idSchema.optional(),
  q: searchQuerySchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
})

export const createTicketRequest = z.object({
  tenantId: idSchema.optional(),
  subject: z.string().trim().min(3).max(200),
  source: ticketSourceSchema,
  raisedByName: z.string().trim().min(2).max(120),
  raisedByUserId: idSchema.optional(),
  category: z.string().trim().max(60).optional(),
  body: noteSchema.optional(),
})

export const patchTicketRequest = z
  .object({
    status: ticketStatusSchema.optional(),
    assignedToUserId: idSchema.nullish(),
    category: z.string().trim().max(60).nullish(),
    subject: z.string().trim().min(3).max(200).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export const createTicketMessageRequest = z.object({
  body: z.string().trim().min(1).max(5000),
  direction: messageDirectionSchema.default('OUT'),
})

export const ticketListResponse = z.array(ticketSchema)
export const ticketMessageListResponse = z.array(ticketMessageSchema)

/** The "what they ask about" tile — categories by volume. */
export const askedAboutResponse = z.array(
  z.object({ label: labelSchema, count: z.number().int().nonnegative() }),
)

export type AskedAboutRow = z.infer<typeof askedAboutResponse>[number]
export type AskedAboutResponse = z.infer<typeof askedAboutResponse>
export type Ticket = z.infer<typeof ticketSchema>
export type TicketMessage = z.infer<typeof ticketMessageSchema>
export type TicketListQuery = z.input<typeof ticketListQuery>
export type CreateTicketRequest = z.input<typeof createTicketRequest>
export type PatchTicketRequest = z.input<typeof patchTicketRequest>
export type CreateTicketMessageRequest = z.input<typeof createTicketMessageRequest>
